<template><div><component-combinator component-name="DtEmojiPicker" /><h2 id="variants-and-examples" tabindex="-1" class="d-docsite--header-2">Variants and Examples <a class="header-anchor d-link d-docsite--link d-link" href="#variants-and-examples" aria-hidden="true">#</a></h2>
<h3 id="default" tabindex="-1" class="d-docsite--header-3">Default <a class="header-anchor d-link d-docsite--link d-link" href="#default" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-emoji-picker
  :skin-tone=&quot;Default&quot;
  skin-selector-button-tooltip-label=&quot;Change default skin tone&quot;
  :tab-set-labels=&quot;[`Most recently used`, `Smileys and people`, ...]&quot;
  :recently-used-emojis=&quot;[
    {
      name: `thumbs up`,
      category: `people`,
      shortname: `:thumbsup:`,
      shortname_alternates: [`:+1:`, `:thumsbup:`],
      keywords: [`+1`, `hand`, `thumb`, `up`, `uc6`],
      unicode_output: `1f44d`,
      unicode_character: `1f44d`,
    },
    {
      name: `thumbs up: medium-light skin tone`,
      ...
    }
    ...
  ]&quot;
  search-results-label=&quot;Search results&quot;
  search-no-results-label=&quot;Search results&quot;
  search-placeholder-label=&quot;Search...&quot;
  @skin-tone=&quot;skinTone = $event&quot;
  @selected-emoji=&quot;selectedEmoji&quot;
/&gt;'>
<dt-emoji-picker
  skin-tone="Default"
  skin-selector-button-tooltip-label="Change default skin tone"
  :tab-set-labels="[
    'Most recently used',
    'Smileys and people',
    'Nature',
    'Food',
    'Activity',
    'Travel',
    'Objects',
    'Symbols',
    'Flags',
  ]"
  :recently-used-emojis="recentlyUsedEmojis"
  search-results-label="Search results"
  search-no-results-label="No results"
  search-placeholder-label="Search..."
/>
</code-example>
<h3 id="with-popover" tabindex="-1" class="d-docsite--header-3">With Popover <a class="header-anchor d-link d-docsite--link d-link" href="#with-popover" aria-hidden="true">#</a></h3>
<code-example source-code='&lt;dt-popover
  :open=&quot;emojiPickerOpened&quot;
  initial-focus-element=&quot;#searchInput&quot;
  padding=&quot;none&quot;
  @opened=&quot;(open) =&gt; { emojiPickerOpened = open }&quot;
&gt;
  &lt;template #anchor&gt;
    &lt;dt-button
      :size=&quot;200&quot;
      circle
      importance=&quot;clear&quot;
      @click=&quot;toggleEmojiPicker&quot;
    &gt;
      &lt;template #startIcon&gt;
        &lt;dt-icon
          name=&quot;satisfied&quot;
          size=&quot;300&quot;
        /&gt;
      &lt;/template&gt;
    &lt;/dt-button&gt;
  &lt;/template&gt;
  &lt;template #content&gt;
    &lt;dt-emoji-picker
      skin-tone=&quot;Default&quot;
      skin-selector-button-tooltip-label=&quot;Change default skin tone&quot;
      :tab-set-labels=&quot;[
        &#39;Most recently used&#39;,
        &#39;Smileys and people&#39;,
        &#39;Nature&#39;,
        &#39;Food&#39;,
        &#39;Activity&#39;,
        &#39;Travel&#39;,
        &#39;Objects&#39;,
        &#39;Symbols&#39;,
        &#39;Flags&#39;,
      ]&quot;
      :recently-used-emojis=&quot;recentlyUsedEmojis&quot;
      search-results-label=&quot;Search results&quot;
      search-no-results-label=&quot;No results&quot;
      search-placeholder-label=&quot;Search...&quot;
    /&gt;
  &lt;/template&gt;
&lt;/dt-popover&gt;'>
<dt-popover
  :open="emojiPickerOpened"
  initial-focus-element="#searchInput"
  padding="none"
  @opened="(open) => { emojiPickerOpened = open }"
>
  <template #anchor>
    <dt-button
      :size="200"
      circle
      importance="clear"
      @click="toggleEmojiPicker"
    >
      <template #startIcon>
        <dt-icon
          name="satisfied"
          size="300"
        />
      </template>
    </dt-button>
  </template>
  <template #content>
    <dt-emoji-picker
      skin-tone="Default"
      skin-selector-button-tooltip-label="Change default skin tone"
      :tab-set-labels="[
        'Most recently used',
        'Smileys and people',
        'Nature',
        'Food',
        'Activity',
        'Travel',
        'Objects',
        'Symbols',
        'Flags',
      ]"
      :recently-used-emojis="recentlyUsedEmojis"
      search-results-label="Search results"
      search-no-results-label="No results"
      search-placeholder-label="Search..."
    />
  </template>
</dt-popover>
</code-example>
<h2 id="vue-api" tabindex="-1" class="d-docsite--header-2">Vue API <a class="header-anchor d-link d-docsite--link d-link" href="#vue-api" aria-hidden="true">#</a></h2>
<component-vue-api component-name="emoji_picker" /><h2 id="usage" tabindex="-1" class="d-docsite--header-2">Usage <a class="header-anchor d-link d-docsite--link d-link" href="#usage" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">We recommend to wrap the emoji picker in a <code v-pre>dt-popover</code> component.</p>
<p class="d-docsite--paragraph">It is important to know the sequence of tab and emoji tabset will <strong>not change</strong>. You should always provide a proper <code v-pre>tabSetLabels</code> array to match the order of the emoji tabset.</p>
<p class="d-docsite--paragraph">The 'Most recently used' tab will always be the first tab in the <code v-pre>tabSetLabels</code> array. It should be updated dynamically by the consumer by using the <code v-pre>recentlyUsedEmojis</code> prop.</p>
<p class="d-docsite--paragraph">The <code v-pre>skin-tone</code> prop provided to the component should listen to the <code v-pre>skin-tone</code> event emitted by the skin selector button to be updated instantly.</p>
<p class="d-docsite--paragraph">You must listen to the <code v-pre>close</code> event to close the emoji picker. This event is emitted by the keyboard navigation on <code v-pre>ESC</code>.</p>
<p class="d-docsite--paragraph">For the <code v-pre>withPopover</code> variant it is necessary to provide the <code v-pre>initial-focus-element=&quot;#searchInput&quot;</code> prop to the <code v-pre>dt-popover</code> component. With this we accomplish the requirement to have the search input focused when the emoji picker is opened.</p>
<h2 id="accessibility" tabindex="-1" class="d-docsite--header-2">Accessibility <a class="header-anchor d-link d-docsite--link d-link" href="#accessibility" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">The emoji picker starts with the focus on the input search to give the user a quick way to search for an emoji.</p>
<p class="d-docsite--paragraph">When the user searches for an emoji, the first emoji in the list will be highlighted, this allow the user to press <code v-pre>ENTER</code> to select this emoji.</p>
<p class="d-docsite--paragraph">From the input search the user can navigate by pressing <code v-pre>UP</code> arrow to the tabset or <code v-pre>DOWN</code> arrow to the emoji list.</p>
<p class="d-docsite--paragraph">From the tabset it is possible to navigate through the tabs with <code v-pre>LEFT</code> and <code v-pre>RIGHT</code> arrows, it will jump again to the input search with <code v-pre>DOWN</code> arrow.</p>
<p class="d-docsite--paragraph">From the emoji list you can navigate through the first emoji of each emoji tabset with <code v-pre>TAB</code>. If you are on the last emoji tabset (flags), it will jump to the skin tone selector. You can navigate through individual emojis and tabsets with the arrow keys.</p>
<p class="d-docsite--paragraph">You can jump to the previous first emoji of the previous emoji tabset with <code v-pre>SHIFT + TAB</code>. If you are on the first emoji tabset, it will jump to the input search.</p>
<p class="d-docsite--paragraph">In the skin tone selector the user can press <code v-pre>ENTER</code> to open the skin tone selector and select the desired skin tone, you can navigate through with <code v-pre>LEFT</code> and <code v-pre>RIGHT</code> arrows and select with <code v-pre>ENTER</code>. This selection will close the skin tone selector and emit the <code v-pre>skin-tone</code> event with the selected skin tone.</p>
<p class="d-docsite--paragraph">From the skin tone selector the user can jump to the tabset selector with <code v-pre>TAB</code> button.</p>
<p class="d-docsite--paragraph">This is the following sequence of the keyboard navigation with <code v-pre>TAB</code>:</p>
<ul class="d-docsite--unordered-list">
<li class="d-docsite--list-element">Input search -&gt; First emoji of the first emoji tabset</li>
<li class="d-docsite--list-element">First emoji tabset -&gt; Next first emoji of the next emoji tabset</li>
<li class="d-docsite--list-element">First emoji of the last emoji tabset -&gt; Skin tone selector</li>
<li class="d-docsite--list-element">Skin tone selector -&gt; Tabset selector</li>
<li class="d-docsite--list-element">Tabset selector -&gt; Input search</li>
</ul>
<p class="d-docsite--paragraph">On <code v-pre>ESC</code> the emoji picker will emit the <code v-pre>close</code> event to close the emoji picker.</p>
</div></template>


<script setup>
import { ref } from 'vue';

const emojiPickerOpened = ref(false);
const toggleEmojiPicker = () => {
  emojiPickerOpened.value = !emojiPickerOpened.value;
};

const recentlyUsedEmojis = [
  {
    name: 'thumbs up',
    category: 'people',
    shortname: ':thumbsup:',
    shortname_alternates: [':+1:', ':thumsbup:'],
    keywords: ['+1', 'hand', 'thumb', 'up', 'uc6'],
    unicode_output: '1f44d',
    unicode_character: '1f44d',
  },
  {
    name: 'thumbs up: medium-light skin tone',
    category: 'people',
    shortname: ':thumbsup_tone2:',
    shortname_alternates: [':+1_tone2:', ':thumsbup_tone2:'],
    keywords: ['+1', 'hand', 'medium-light skin tone', 'thumb', 'up', 'uc8'],
    unicode_output: '1f44d-1f3fc',
    unicode_character: '1f44d-1f3fc',
  },
  {
    name: 'thumbs up: dark skin tone',
    category: 'people',
    shortname: ':thumbsup_tone5:',
    shortname_alternates: [':+1_tone5:', ':thumsbup_tone5:'],
    keywords: ['+1', 'dark skin tone', 'hand', 'thumb', 'up', 'uc8'],
    unicode_output: '1f44d-1f3ff',
    unicode_character: '1f44d-1f3ff',
  },
  {
    name: 'person: light skin tone',
    category: 'people',
    shortname: ':adult_tone1:',
    shortname_alternates: [':adult_light_skin_tone:'],
    keywords: ['gender-neutral', 'light skin tone', 'uc10'],
    unicode_output: '1f9d1-1f3fb',
    unicode_character: '1f9d1-1f3fb',
  },
  {
    name: 'woman with veil: dark skin tone',
    category: 'people',
    shortname: ':woman_with_veil_tone5:',
    shortname_alternates: [':woman_with_veil_dark_skin_tone:'],
    keywords: ['uc13'],
    unicode_output: '1f470-1f3ff-200d-2640-fe0f',
    unicode_character: '1f470-1f3ff-2640',
  },
];

</script>